import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SendIcon from "@mui/icons-material/Send";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ScrollRevealSection from "../common/ScrollRevealSection";
import emailjs from "emailjs-com";

const EmailSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "", // Added subject
    message: "",
  });

  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null },
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({
      submitted: false,
      submitting: true,
      info: { error: false, msg: null },
    });

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formData,
        process.env.REACT_APP_EMAILJS_USER_ID
      );
      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: "Message sent successfully!" },
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: "Something went wrong. Please try again." },
      });
    }
  };

  return (
    <ScrollRevealSection>
      <Container id="contact" sx={{ py: 10 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                mb: 3,
                background: "linear-gradient(90deg, #FFFFFF 0%, #A78BFA 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Get In Touch
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ color: "rgba(255,255,255,0.7)", mb: 4 }}
            >
              I'm currently looking for new opportunities to collaborate on
              projects. Whether you have a question or just want to say hi, I'll
              try my best to get back to you!
            </Typography>

            <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
              <Button
                variant="outlined"
                href="https://github.com/abhi-ramtel"
                target="_blank"
                startIcon={<GitHubIcon />}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": {
                    borderColor: "#7C3AED",
                    color: "#7C3AED",
                  },
                }}
              >
                GitHub
              </Button>

              <Button
                variant="outlined"
                href="https://linkedin.com/in/abhi-ramtel"
                target="_blank"
                startIcon={<LinkedInIcon />}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "rgba(255,255,255,0.7)",
                  "&:hover": {
                    borderColor: "#7C3AED",
                    color: "#7C3AED",
                  },
                }}
              >
                LinkedIn
              </Button>

            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 4,
                backgroundColor: "rgba(18, 18, 18, 0.8)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 2,
              }}
            >
              <TextField
                fullWidth
                label="Name"
                name="name"
                variant="outlined"
                margin="normal"
                required
                value={formData.name}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "rgba(255,255,255,0.9)", // This sets the input text color
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#7C3AED",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.7)", // This sets the label color
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#7C3AED", // This sets the focused label color
                  },
                }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                variant="outlined"
                margin="normal"
                required
                value={formData.email}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "rgba(255,255,255,0.9)", // This sets the input text color
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#7C3AED",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.7)", // This sets the label color
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#7C3AED", // This sets the focused label color
                  },
                }}
              />

              <TextField
                fullWidth
                label="Subject"
                name="subject"
                variant="outlined"
                margin="normal"
                required
                value={formData.subject}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "rgba(255,255,255,0.9)",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#7C3AED",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.7)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#7C3AED",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Message"
                name="message"
                variant="outlined"
                margin="normal"
                required
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "rgba(255,255,255,0.9)", // This sets the input text color
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#7C3AED",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,0.7)", // This sets the label color
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#7C3AED", // This sets the focused label color
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                disabled={status.submitting}
                sx={{
                  py: 1.5,
                  backgroundColor: "rgba(124, 58, 237, 0.8)",
                  color: "#121212", // This already sets the text color to dark
                  "&:hover": {
                    backgroundColor: "#7C3AED",
                  },
                  // Add styles for the disabled state (when "Sending...")
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(124, 58, 237, 0.5)",
                    color: "#121212", // Keep text color consistent when disabled
                  },
                }}
              >
                {status.submitting ? "Sending..." : "Send Message"}
              </Button>

              {status.info.msg && (
                <Typography
                  sx={{
                    mt: 2,
                    color: status.info.error ? "#ff6b6b" : "#7C3AED",
                  }}
                >
                  {status.info.msg}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </ScrollRevealSection>
  );
};

export default EmailSection;
